import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '@/integrations/supabase/client';
import { Loader2, CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface VerificationState {
  status: 'loading' | 'success' | 'error' | 'expired' | 'used';
  message: string;
  customer?: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    organization_id: string;
  };
}

export function MagicLinkVerification() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [verificationState, setVerificationState] = useState<VerificationState>({
    status: 'loading',
    message: 'Verifying your magic link...'
  });

  const token = searchParams.get('token');
  // Only allow same-origin relative redirects (starts with `/` and no `//` or protocol).
  // Strip any legacy /portal prefix — router basename handles it now.
  const rawRedirect = searchParams.get('redirect') || '/';
  const isSafe = rawRedirect.startsWith('/') && !rawRedirect.startsWith('//');
  const stripped = rawRedirect.replace(/^\/portal(\/|$)/, '/');
  const redirectUrl = isSafe ? stripped : '/';

  useEffect(() => {
    if (!token) {
      setVerificationState({
        status: 'error',
        message: 'Invalid or missing verification token.'
      });
      return;
    }

    verifyMagicLink();
  }, [token]);

  const verifyMagicLink = async () => {
    try {
      setVerificationState({
        status: 'loading',
        message: 'Verifying your magic link...'
      });

      // Get the session to include auth headers
      const { data: { session } } = await supabase.auth.getSession();
      
      // Make direct fetch call to the edge function
      const response = await fetch(`${SUPABASE_URL}/functions/v1/verify-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token || SUPABASE_ANON_KEY}`,
          'apikey': SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Magic link verification error:', data);
        
        // Extract specific error message from response
        const errorMessage = data.error || data.message || 'Failed to verify magic link';
        
        if (errorMessage.toLowerCase().includes('expired')) {
          setVerificationState({
            status: 'expired',
            message: 'This magic link has expired. Please request a new one.'
          });
        } else if (errorMessage.toLowerCase().includes('already been used')) {
          setVerificationState({
            status: 'used',
            message: 'This magic link has already been used. Please request a new one if needed.'
          });
        } else {
          setVerificationState({
            status: 'error',
            message: errorMessage
          });
        }
        return;
      }

      if (data?.success && data?.customer) {
        setVerificationState({
          status: 'success',
          message: `Welcome back, ${data.customer.first_name || data.customer.email}!`,
          customer: data.customer
        });

        // Store customer session data
        localStorage.setItem('customer_session', JSON.stringify({
          customer: data.customer,
          session_token: data.session_token,
          expires_at: data.session_expires_at,
          authenticated_at: new Date().toISOString()
        }));

        // Redirect after a short delay to show success message
        setTimeout(() => {
          // Parse the redirect URL to extract only the pathname
          try {
            const url = new URL(redirectUrl, window.location.origin);
            navigate(url.pathname, { replace: true });
          } catch {
            // Fall back to dashboard — router basename handles any /portal prefix.
            navigate('/dashboard', { replace: true });
          }
        }, 2000);
      } else {
        setVerificationState({
          status: 'error',
          message: 'Invalid response from verification service.'
        });
      }
    } catch (error: any) {
      console.error('Verification error:', error);
      setVerificationState({
        status: 'error',
        message: 'An unexpected error occurred. Please try again.'
      });
    }
  };

  const handleRetry = () => {
    if (token) {
      verifyMagicLink();
    }
  };

  const handleRequestNewLink = () => {
    navigate('/', { replace: true });
  };

  const getIcon = () => {
    switch (verificationState.status) {
      case 'loading':
        return <Loader2 className="h-12 w-12 animate-spin text-primary" />;
      case 'success':
        return <CheckCircle className="h-12 w-12 text-green-500" />;
      case 'error':
      case 'expired':
      case 'used':
        return <XCircle className="h-12 w-12 text-red-500" />;
      default:
        return null;
    }
  };

  const getTitle = () => {
    switch (verificationState.status) {
      case 'loading':
        return 'Verifying Magic Link';
      case 'success':
        return 'Welcome Back!';
      case 'expired':
        return 'Link Expired';
      case 'used':
        return 'Link Already Used';
      case 'error':
        return 'Verification Failed';
      default:
        return 'Magic Link Verification';
    }
  };

  const getDescription = () => {
    switch (verificationState.status) {
      case 'loading':
        return 'Please wait while we verify your access...';
      case 'success':
        return 'You will be redirected to your portal shortly.';
      case 'expired':
        return 'Magic links expire for security reasons. Please request a new one.';
      case 'used':
        return 'Each magic link can only be used once. Please request a new one if needed.';
      case 'error':
        return 'We encountered an issue verifying your link.';
      default:
        return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/50 to-muted/30 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-4">
          <div className="flex justify-center">
            {getIcon()}
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">{getTitle()}</CardTitle>
            <CardDescription className="text-base mt-2">
              {getDescription()}
            </CardDescription>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              {verificationState.message}
            </AlertDescription>
          </Alert>

          {verificationState.status === 'success' && verificationState.customer && (
            <div className="text-center space-y-2">
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">Signing in as:</p>
                <p className="font-semibold">{verificationState.customer.email}</p>
              </div>
              <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Redirecting to your portal...
              </div>
            </div>
          )}

          {(verificationState.status === 'error') && (
            <div className="space-y-3">
              <Button 
                onClick={handleRetry} 
                variant="outline" 
                className="w-full"
                disabled={!token}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button 
                onClick={handleRequestNewLink} 
                className="w-full"
              >
                Request New Magic Link
              </Button>
            </div>
          )}

          {(verificationState.status === 'expired' || verificationState.status === 'used') && (
            <Button 
              onClick={handleRequestNewLink} 
              className="w-full"
            >
              Request New Magic Link
            </Button>
          )}

          {verificationState.status === 'loading' && (
            <div className="text-center">
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  This usually takes just a moment...
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}